import React from 'react';
import * as BABYLON from 'babylonjs';
import useEffectOnce from "./utils/UseEffectOnce";
import useEffectPartialDeps from "./utils/UseEffectPartialDeps";
import {checkWin} from "./utils/gameMaster";

const generateGrid = (gridSize) => {
    const a1 = []
    for (let i = 0; i < gridSize; i++) {
        const a2 = [];
        for (let j = 0; j < gridSize; j++) {
            const a3 = []
            for (let k = 0; k < gridSize; k++) {
                a3[k] = {
                    owner: null
                }
            }
            a2[j] = a3;
        }
        a1[i] = a2;
    }
    return a1;
}

let done,
    picking,
    grid,
    scene,
    engine,
    hl,
    red,
    blue,
    highlighted = null,
    cellSize = null,
    squares = null,
    materials = {};

const getCoordsById = id => id.split(',').map(asdf =>  parseInt(asdf));
const getCellById = id => getCell(getCoordsById(id))
const getCell = ([x, y, z]) => grid[x][y][z]

export const Grid = ({
                         gridSize,
                         squaresProp,
                         cellSizeProp,
                         pickingProp,
                         setPicking,
                         setOnCellClaimed,
                         onCellClick
                     }) => {

    const mapCells = (func) => {
        for (let i = 0; i < gridSize; i++) {
            for (let j = 0; j < gridSize; j++) {
                for (let k = 0; k < gridSize; k++) {
                    func(i, j, k);
                }
            }
        }
    }

    const clearHighlight = () => {
        if (highlighted) {
            hl.removeMesh(highlighted);
            highlighted = null;
        }
    }

    const setHighlight = mesh => {
        clearHighlight();
        hl.addMesh(mesh, picking === 'red' ? red : blue);
        highlighted = mesh;
    }

    const onCellClaimed = (coords, claimedBy) => {
        clearHighlight();
        const cell = getCell(coords);
        cell.owner = picking;
        cell.mesh.material = getMat(picking);

        const winnerRow = checkWin(
            picking,
            getCell,
            coords,
            grid,
            gridSize
        );

        if (winnerRow) {
            done = true;
            highlightWinnerRow(winnerRow);
        } else {
            picking = claimedBy === 'red' ? 'blue' : 'red';
            setPicking(picking);
        }
    }

    const indexToSpace = (index) => 2 * (index - ((gridSize - 1) / 2))

    let getMat = (color) => materials[color || 'grey'];

    const createMesh = (xIndex, yIndex, zIndex) => {
        // get the relevant cell
        const cell = getCell([xIndex, yIndex, zIndex]);
        // delete any existing mesh for the cell
        cell.mesh && cell.mesh.dispose();
        // generate the id for the mesh
        const meshId = `${xIndex}, ${yIndex}, ${zIndex}`;
        // create the mesh
        const mesh = squares
            ? new BABYLON.Mesh.CreateBox(meshId, cellSize, scene)
            : BABYLON.MeshBuilder.CreateSphere(meshId, {diameter: cellSize}, scene);
        // position the mesh
        mesh.position = new BABYLON.Vector3(
            indexToSpace(yIndex),
            indexToSpace(xIndex),
            indexToSpace(zIndex)
        );
        // handle selection highlight
        if (highlighted && highlighted.name === meshId)
            setHighlight(mesh);
        // if game is over and the cell was no in the winning row, it fades
        const fadedPostfix = done && !cell.winner ? 'Faded' : '';
        // construct the appropriate key for the cell material
        const materialKey = `${cell.owner || 'grey'}${fadedPostfix}`;
        mesh.material = getMat(materialKey);
        cell.mesh = mesh;
        return mesh;
    }

    useEffectOnce(() => {
        // pass callback function to app
        setOnCellClaimed(onCellClaimed);
        setup();
        generateCells();
        engine.runRenderLoop(() => scene.render());
    }, [])

    const setup = function () {
        done = false;
        // generate an "empty" grid based on gridSize
        grid = generateGrid(gridSize);

        // Initialize the BABYLON scene
        let canvas = document.getElementById("main");
        engine = new BABYLON.Engine(canvas, true, { stencil: true });
        scene = new BABYLON.Scene(engine);

        cellSize = cellSizeProp;
        squares = squaresProp;
        picking = pickingProp;

        // add highlight layer for 2 step cell selection
        hl = new BABYLON.HighlightLayer("hl1", scene);
        //define team colors
        red = new BABYLON.Color3(.8, 0, 0);
        blue = new BABYLON.Color3(0, .4, .8);

        // create red materials: faded and normal
        const redMat = materials.red = new BABYLON.StandardMaterial("myMat", scene);
        redMat.ambientColor = new BABYLON.Color3(.8, 0, 0);
        const redFadedMat = materials.redFaded = new BABYLON.StandardMaterial("myMat", scene);
        redFadedMat.ambientColor = new BABYLON.Color3(.8, 0, 0);
        redFadedMat.alpha = .3

        // create blue materials: faded and normal
        const blueMat = materials.blue = new BABYLON.StandardMaterial("myMat", scene);
        blueMat.ambientColor = new BABYLON.Color3(0, .3, .6);
        const blueFadedMat = materials.blueFaded = new BABYLON.StandardMaterial("myMat", scene);
        blueFadedMat.ambientColor = new BABYLON.Color3(0, .3, .6);
        blueFadedMat.alpha = .3

        // create neutral materials: faded and normal
        const mat = materials.grey = new BABYLON.StandardMaterial("myMat", scene);
        mat.alpha = .7;
        const fadedMat = materials.greyFaded = new BABYLON.StandardMaterial("myMat", scene);
        fadedMat.alpha = .3;

        // add the camera, rotates at a fixed distance around the center of the scene
        let camera = new BABYLON.ArcRotateCamera("arcCamera1",
            0,
            0,
            16 + (gridSize * 3),
            BABYLON.Vector3(0, 0, -10),
            scene
        );

        // allow camera to rotate all the way around
        camera.lowerBetaLimit = null;
        camera.upperBetaLimit = null;
        camera.lowerAlphaLimit = null;
        camera.upperAlphaLimit = null;
        camera.allowUpsideDown = true;

        // prevent zooming through 0 resulting in weird mirrored behaviour
        camera.lowerRadiusLimit = 0;
        camera.upperRadiusLimit = camera.radius + 3;

        // better pinch zooming on touch screens
        camera.useNaturalPinchZoom = true;

        // enable camera controls: mouse and touch
        camera.attachControl(canvas, false);
        camera.setPosition(new BABYLON.Vector3(50, 0, 0));
        // camera.setPosition(new BABYLON.Vector3(50, 50, 50));

        scene.ambientColor = new BABYLON.Color3(1, 1, 1);
        scene.clearColor = new BABYLON.Color3(0.73, 0.73, 0.7);

        // light the scene from above
        let light = new BABYLON.HemisphericLight("hemilight", new BABYLON.Vector3(.5,1,0), scene);
        light.intensity = 0.5;

    };

    const generateCells = () => {
        // set up "onClick" listener
        scene.onPointerPick = (evt, pickResult) => {
            // go home if the game is over
            if (done) return;
            if (pickResult.hit && pickResult.pickedMesh) {
                const cell = getCellById(pickResult.pickedMesh.name);
                // if the cell is already claimed, do nothing
                if (cell.owner)
                    return;
                // highlight the selected cell
                setHighlight(cell.mesh);
                // trigger external game logic to commit selection
                onCellClick(getCoordsById(pickResult.pickedMesh.name))
            }
        };

        // create all initial cell meshes
        mapCells(createMesh);

    }

    // util function to check if cell `target` is contained in row `scope`
    const contains = (scope, target) => {
        const equal = (ary1, ary2) => {
            if (ary1.length !== ary2.length) return false;
            for (let i = 0; i < ary1.length; i++) {
                if (ary1[i] !== ary2[i]) return false;
            }
            return true;
        }
        for (let i = 0; i < scope.length; i++) {
            if (equal(scope[i], target)) return true;
        }
        return false;
    }

    // "highlight" winner row by fading all other cells
    const highlightWinnerRow = (winnerRow) => {
        mapCells((xIndex, yIndex, zIndex) => {
            if (contains(winnerRow, [xIndex, yIndex, zIndex]))
                grid[xIndex][yIndex][zIndex].winner = true;
        })
        mapCells(createMesh);
    }

    // refresh the size of a given cell based on `cellSize`
    const resizeCell = (xIndex, yIndex, zIndex) =>
        grid[xIndex][yIndex][zIndex].mesh.scaling = new BABYLON.Vector3(1, 1, 1).scale(cellSize);

    // refresh size of all cells when `cellSizeProp` changes
    useEffectPartialDeps(() => {
        if (cellSizeProp === cellSize) return;
        cellSize = cellSizeProp;
        mapCells(resizeCell);
    }, [cellSizeProp])

    useEffectPartialDeps(() => {
        if (squaresProp === squares) return;
        squares = squaresProp;
        mapCells(createMesh);
    }, [squaresProp])

    return (
        <canvas id="main"/>
    );
}